/**
 * The read side of the derived timing schema.
 *
 * These mirror what timing/internal/store writes. They are separate from that
 * package's Go structs by necessity and kept deliberately thin: no formatting,
 * no derived values, just the field names as they land in Firestore. Anything
 * computed belongs in the widget that computes it, so that adding a display
 * does not change the contract between the pipeline and the app.
 *
 * Times are milliseconds throughout — the pipeline stores no formatted strings,
 * because they were about a third of a lap document and bought no capability.
 */

import type { Timestamp } from 'firebase/firestore'
import type { ClockCheckpointDoc } from './clock'

/**
 * A bucket of timing data, identified by what the stream itself said rather
 * than by any configured event. See timing/internal/store/streamsession.go.
 */
export interface StreamSessionDoc {
  trackId?: string
  /**
   * The race weekend, as the stream names it — "Hankook 25 Hours Of Spa".
   *
   * The one label that stays put: one RIS feed was observed serving two
   * different championships in an afternoon at the same meeting, so the series
   * name is not a stable handle on which event a bucket belongs to.
   */
  meetingNameRaw?: string
  /**
   * The configured event this bucket belongs to.
   *
   * Handed down by the reconciler, which knew which event it was starting a
   * capture for — not matched from names afterwards. That is what makes
   * "what is live for this event" answerable. Absent for a manual backfill.
   */
  eventId?: string
  seriesNameRaw?: string
  sessionNameRaw?: string
  /** The session's own name, where the vendor gives one. */
  name?: string
  status?: string
  trackState?: string
  clockStartAt?: Timestamp
  /** The clock anchor the browser counts from. Absent until a clock is seen. */
  clock?: ClockCheckpointDoc
  firstSeenAt?: Timestamp
  /**
   * Refreshed on every poll, which is how a live stream is told from a frozen
   * one — and, because it is stamped by the capture job, the reference the
   * viewer's own clock is corrected against.
   */
  lastSeenAt?: Timestamp
  /**
   * When the race was observed to end, where it could be.
   *
   * Written once by the interpreter, from the countdown running out or the
   * stream saying FINISHED — not from a timetable, and not from capture
   * stopping, which happens hours later. Absent for a session nobody watched
   * the end of, and readers then fall back to the last lap anybody completed.
   */
  finishedAt?: Timestamp
  /**
   * Every car number this bucket has carried, accumulated across the session.
   *
   * Recorded so a team can recognise which of a weekend's sessions are theirs
   * when the series names do not line up, and so equally-fresh buckets can be
   * ranked by completeness — one race carried on several channels produces
   * several buckets, and the one with the whole field is the one to open.
   *
   * Never an inclusion rule on its own: numbers repeat across series at one
   * meeting. See packages/shared/src/relevance.ts.
   */
  cars?: string[]
  /**
   * How many of those cars this channel has ever reported pit information for.
   *
   * The second dimension of completeness, and the one car count cannot see. At
   * Zandvoort the site root and /funbe both carried all thirty-six cars of the
   * same practice; the root reported pit data for one of them and /funbe for
   * thirty-one, so a dashboard that opened the root showed one stint per car
   * all session because nothing in that feed ever ends one.
   *
   * Absent on anything captured before this was recorded, which reads as
   * "unknown" rather than "none" — see comparableOn in relevance.ts.
   */
  carsWithPitData?: number
}

/**
 * One car's session bests, as the interpreter materialises them at
 * streamSessions/{id}/carSummaries/{car}. Minima throughout, so replay-safe.
 *
 * Here with the other Firestore document types rather than in compare.ts,
 * where it was born: it is read by the dashboard and the stats page as well as
 * by the comparison, and a document shape that lives in the module of one of
 * its readers gets imported from a surprising place by the rest.
 */
/**
 * One refuelling, matched against this session by the fuel job.
 *
 * Kept at streamSessions/{id}/fuel/{car-time}. The raw fill is the fuel
 * station's; everything below `kind` is our reading of it, and is rewritten
 * whenever the matching is rerun.
 */
export interface FuelDoc {
  car: string
  at: Timestamp
  litres: number
  source: string
  /** The station's own rendering of the time, as captured. */
  raw?: string
  /**
   * stop: during a pit stop. after: once the car had stopped running — the
   * end-of-session fill-up. before: the tank it started with. unmatched: in
   * the session but nowhere near a stop.
   */
  kind: 'stop' | 'after' | 'before' | 'unmatched'
  /** The stop it went in during. */
  stopNumber?: number
  /** Laps the car had completed when the fuel went in. */
  lap?: number
  /** Laps since the previous fill, or since the start where sinceStart is set. */
  lapsSincePrevious?: number
  sinceStart?: boolean
  litresPerLap?: number
  /** Laps run on this fuel: to the next fill, or so far where open is set. */
  lapsOnFuel?: number
  open?: boolean
}

/** One car's bests for one session, as the interpreter materialises them. */
export interface CarSummaryDoc {
  car: string
  eventId?: string
  sessionId?: string
  class?: string
  bestLapMs?: number | null
  bestLapNum?: number
  bestS1Ms?: number | null
  bestS1Lap?: number
  bestS2Ms?: number | null
  bestS2Lap?: number
  bestS3Ms?: number | null
  bestS3Lap?: number
  idealLapMs?: number | null
  laps?: number
  updatedAt?: unknown
}

/** One car in the live standings. */
export interface CarStanding {
  number: string
  position: number
  driver?: string
  team?: string
  /** As the vendor spells it: RIS calls it a category, TSL a primary class. */
  class?: string
  /** Derived by the interpreter, because neither vendor supplies it. */
  classPosition?: number
  lap?: number
  state?: string
  lastLapMs?: number | null
  /** Derived by the interpreter, and inherited across a capture handover. */
  bestLapMs?: number | null
  /** Sectors of the lap in progress, filling in a third at a time. */
  s1Ms?: number | null
  s2Ms?: number | null
  s3Ms?: number | null
  gapToLeaderMs?: number | null
  gapToLeaderLaps?: number
  intervalAheadMs?: number | null
  intervalAheadLaps?: number
  stops?: number | null
}

/**
 * The whole grid in one document at live/standings.
 *
 * One document rather than one per car: per-car documents would be around a
 * hundred writes a second on a full grid, and the client would need a query
 * rather than a single listener.
 */
export interface StandingsDoc {
  updatedAt?: Timestamp
  cars?: CarStanding[]
}

/**
 * A lap.
 *
 * Written while in progress as sectors land, then completed — so a document
 * with sector times and no lapTimeMs is the lap the car is on now, not a lap
 * that lost its time.
 */
export interface LapDoc {
  car: string
  lap: number
  startedAt?: Timestamp
  completedAt?: Timestamp
  lapTimeMs?: number | null
  s1Ms?: number | null
  s2Ms?: number | null
  s3Ms?: number | null
  s1Fcy?: boolean
  s2Fcy?: boolean
  s3Fcy?: boolean
  s1Sz?: boolean
  s2Sz?: boolean
  s3Sz?: boolean
  inLap?: boolean
  outLap?: boolean
  stintNumber?: number
  position?: number
  driver?: string
  class?: string
  intervalAheadMs?: number | null
  intervalAheadLaps?: number
  gapToLeaderMs?: number | null
  gapToLeaderLaps?: number
}

/**
 * What race control is showing now, at live/messages.
 *
 * Distinct from the messages collection, which is the log. Nothing records when
 * a message left the board, so age cannot tell a live message from an old one —
 * this is the set the feed is presently displaying.
 */
export interface LiveMessagesDoc {
  updatedAt?: Timestamp
  messages?: LiveMessage[]
}

export interface LiveMessage {
  id: string
  at: string
  message: string
  /** Session time when it first appeared, not when it was last re-stated. */
  elapsedMs?: number
}

/** A race-director message, as logged. */
export interface MessageDoc {
  message: string
  elapsedMs?: number
  /**
   * When race control issued it, as the vendor stamped it.
   *
   * Absent on anything captured before this was stored, and on a message whose
   * stamp would not parse — readers fall back to receivedAt, which is our own
   * poll and a second or two late.
   */
  at?: Timestamp
  receivedAt?: Timestamp
}

/**
 * A neutralisation — full course yellow, safety car or a slow zone.
 *
 * Open while `endedAt` is absent, which is what lets the stint chart draw one
 * that is still running.
 */
/**
 * One completed pit stop, at streamSessions/{id}/pitStops/{car}-{n}.
 *
 * The vendor's own pit_in_time and pit_out_time are carried where they exist,
 * but they are usually absent — RIS sent neither for a single stop of the Spa
 * archive. What is always there is `completedAt`, the poll at which the stop
 * was seen to finish, and `stopLengthMs`, the vendor's own duration. Those two
 * give pit-out and pit-in to within a poll interval, which is seconds against
 * the limits anybody measures with them.
 */
export interface PitStopDoc {
  car: string
  stopNumber: number
  /** The lap the car came in on. */
  lap: number
  stopLengthMs?: number | null
  completedAt?: Timestamp
  /** Whoever brought the car in, as the vendor named them. */
  driver?: string
  pitInTime?: string
  pitOutTime?: string
  /**
   * The stop was inferred because the car left the pits without ever tripping
   * the entry sensor. Common — two of three stops in the archive sample — so
   * anything derived from these is less certain and should say so rather than
   * quietly claiming the same precision.
   */
  isRecovery?: boolean
  /** Set only where the timing system explicitly reported a FUEL state.
   *  Absence is not evidence that no fuel was taken. */
  isFuelStop?: boolean
}

export interface FcyPeriodDoc {
  startedAt?: Timestamp
  endedAt?: Timestamp | null
  /** The vendor's track state at the time: FCY, SC, SZ1… */
  state?: string
}

/**
 * One car's stints, at streamSessions/{id}/stints/{car}.
 *
 * Materialised by the interpreter because a stint chart needs every lap of
 * every car — tens of thousands of documents for a 25-hour race, which is not a
 * query to run each time somebody opens a screen. It adds no judgement: it
 * groups on the stintNumber the detection machine already stamped on each lap.
 *
 * One document per car rather than per stint, so a reprocess that yields fewer
 * stints cannot leave orphans behind for a chart to draw.
 */
export interface StintsDoc {
  car: string
  updatedAt?: Timestamp
  stints?: Stint[]
}

export interface Stint {
  /** As stamped on the laps. RIS counts from zero, so a display adds one. */
  number: number
  firstLap: number
  lastLap: number
  startedAt?: Timestamp
  /** Absent while the stint is open. */
  endedAt?: Timestamp | null
  open?: boolean

  laps: number
  totalMs: number
  /** Laps with no in/out lap and no sector under a yellow — the comparable ones. */
  greenLaps: number
  greenTotalMs: number
  bestLapMs?: number | null

  /**
   * How many laps each name the vendor gave was on.
   *
   * Counts rather than one answer, because the vendor is told about a driver
   * change late — sometimes not at all — so the first laps of a stint routinely
   * carry the outgoing driver. Preserving the disagreement is what lets a screen
   * say "uncertain", and what leaves room for the team's declaration to win.
   */
  driverLaps?: Record<string, number>
}

/** The average a stint was run at, over whichever laps the caller means. */
export function stintAverageMs(stint: Stint, only: 'green' | 'all' = 'green'): number | null {
  const laps = only === 'green' ? stint.greenLaps : stint.laps
  const total = only === 'green' ? stint.greenTotalMs : stint.totalMs
  // No stored average, so this is where the opinion lives — and it can change
  // without reprocessing anything.
  return laps > 0 ? Math.round(total / laps) : null
}

/**
 * Whom the vendor thought was driving, and whether it was sure.
 *
 * The most-driven name wins. `certain` is false when the winning name was on
 * fewer than two-thirds of the named laps — when the stint is genuinely
 * split between names rather than merely starting with the wrong one.
 *
 * It used to be false whenever a second name appeared at all. That marked
 * almost every stint, because the timing desk is told about a change late and
 * the first lap or two of a stint routinely carry the outgoing driver; a
 * warning on every card is a warning on none. A stint that is two-thirds one
 * name has settled; one that is split down the middle has not.
 */
export function stintVendorDriver(stint: Stint): { name: string | null; certain: boolean } {
  const entries = Object.entries(stint.driverLaps ?? {})
  if (entries.length === 0) return { name: null, certain: false }
  const best = entries.reduce((a, b) => (b[1] > a[1] ? b : a))
  const total = entries.reduce((n, [, laps]) => n + laps, 0)
  return { name: best[0], certain: best[1] * 3 >= total * 2 }
}

/**
 * Track state, normalised from whatever the vendor called it.
 *
 * Track state is the one place on a dashboard where colour carries meaning, so
 * it gets a closed set rather than a raw string: an unrecognised value has to
 * read as "unknown" rather than silently pick up the styling of whichever
 * branch happened to be last.
 */
export type TrackStatus = 'green' | 'yellow' | 'fcy' | 'safetyCar' | 'red' | 'finished' | 'unknown'

export interface TrackStatusInfo {
  status: TrackStatus
  label: string
}

const trackStatusByCode: Record<string, TrackStatusInfo> = {
  GREEN: { status: 'green', label: 'Green' },
  YELLOW: { status: 'yellow', label: 'Yellow' },
  FCY: { status: 'fcy', label: 'Full course yellow' },
  SC: { status: 'safetyCar', label: 'Safety car' },
  SZ1: { status: 'yellow', label: 'Slow zone 1' },
  SZ2: { status: 'yellow', label: 'Slow zone 2' },
  SZ3: { status: 'yellow', label: 'Slow zone 3' },
  RED: { status: 'red', label: 'Red flag' },
  // What the feed actually sends. Confirmed against a live channel and against
  // 2,790 snapshots at the end of an archived race.
  CHK: { status: 'finished', label: 'Chequered' },
  CHEQUERED: { status: 'finished', label: 'Chequered' },
  FINISHED: { status: 'finished', label: 'Finished' },
}

/**
 * Read a vendor's track state.
 *
 * Unrecognised values become 'unknown' and keep the vendor's own text as the
 * label, which is more useful than "unknown" on its own: it says the pipeline
 * saw something and did not recognise it, rather than that nothing was seen.
 */
export function trackStatus(raw: string | undefined | null): TrackStatusInfo {
  const code = (raw ?? '').trim().toUpperCase()
  if (!code) return { status: 'unknown', label: 'No data' }
  const known = trackStatusByCode[code]
  if (known) return known

  // Simultaneous slow zones, which the interpreter joins as "SZ1+SZ2". They
  // are not one state each: a circuit can neutralise two sectors at once and
  // leave the third green, and a board reading "Slow zone 1" while two are out
  // is worse than no board.
  const zones = code
    .split('+')
    .map((part) => /^SZ([123])$/.exec(part.trim())?.[1])
    .filter((n): n is string => !!n)
  if (zones.length > 1 && zones.length === code.split('+').length) {
    return {
      status: 'yellow',
      label: `Slow zones ${zones.slice(0, -1).join(', ')} & ${zones[zones.length - 1]}`,
    }
  }

  return { status: 'unknown', label: code }
}

/*
sessionTrackStatus is track state as a screen should show it, which is not
always the last thing the vendor said.

The canonical state set has no chequered flag in it, and a stream is under no
obligation to send one. At Le Castellet the six-hour race ended with the state
still reading GREEN on one channel and blank on the other, and both stayed that
way for the rest of the afternoon — so a board lit green for a race that
finished three hours ago is not a stale reading, it is a wrong one, and it is
the part of the screen people glance at rather than read.

finishedAt is the thing we do know: decided from the countdown reaching zero or
from the stream saying so, and worth more here than whatever colour the vendor
happened to leave on the board.
*/
export function sessionTrackStatus(
  session: Pick<StreamSessionDoc, 'trackState' | 'finishedAt'> | null | undefined,
): TrackStatusInfo {
  if (session?.finishedAt) return { status: 'finished', label: 'Finished' }
  return trackStatus(session?.trackState)
}

/*
isInPits reports whether the timing system is telling us where this car is,
rather than leaving us to wonder.

The canonical states are RUN, IN, OUT, FUEL and unknown. Three of those mean
the car is off the racing line for a reason the timekeeper can see: IN is in
the pits, FUEL is stationary being fuelled, and OUT is leaving the pit lane on
an out-lap. Only RUN — or nothing at all — means the car should be out there
lapping.

The distinction matters wherever a screen is about to raise an alarm for a car
that is late. A car nobody can account for being late is the thing worth
knowing; a car in its own pit box being late is just a pit stop, and a warning
about it is noise that teaches people to stop reading the warnings.
*/
export function isInPits(car: Pick<CarStanding, 'state'> | undefined | null): boolean {
  const s = car?.state?.trim().toUpperCase()
  return s === 'IN' || s === 'OUT' || s === 'FUEL'
}

/** Whether a car is running, as opposed to stopped, in the pits, or retired. */
export function isRunning(car: Pick<CarStanding, 'position' | 'state'>): boolean {
  return (car.position ?? 0) > 0
}

/**
 * The last time a car crossed the line, from its laps.
 *
 * From the laps rather than the standings, because the standings document has
 * no per-car timestamp. `completedAt` is the lap end the vendor reported, which
 * is the crossing. For a car that has gone into the pits this is also the best
 * available estimate of when it did: the pit-in loop is not in the data, and
 * the line is seconds from it.
 */
export function lastCrossingOf(laps: readonly LapDoc[], car: string | null): Date | null {
  if (!car) return null
  let latest: Date | null = null
  for (const l of laps) {
    if (l.car !== car) continue
    const at = l.completedAt?.toDate() ?? null
    if (at && (latest == null || at > latest)) latest = at
  }
  return latest
}

/** The fastest lap anyone has set, for marking a personal best as the session best. */
export function sessionBestLapMs(cars: readonly CarStanding[]): number | null {
  let best: number | null = null
  for (const c of cars) {
    const v = c.bestLapMs
    if (v != null && v > 0 && (best === null || v < best)) best = v
  }
  return best
}

/** One car's standing, or undefined when it is not in the session. */
export function findCar(
  standings: StandingsDoc | null | undefined,
  car: string | null,
): CarStanding | undefined {
  if (!standings?.cars || !car) return undefined
  const want = car.trim()
  // Compared literally, as everywhere else: "7" and "07" are different cars to
  // a timekeeper, and folding them together would put one car's data on
  // another.
  return standings.cars.find((c) => c.number?.trim() === want)
}

/** How many cars are actually running, which is what "of N" means. */
export function runningCount(standings: StandingsDoc | null | undefined): number {
  return (standings?.cars ?? []).filter((c) => isRunning(c)).length
}

/** How many cars in a class are running. */
export function classRunningCount(
  standings: StandingsDoc | null | undefined,
  className: string | undefined,
): number {
  if (!className) return 0
  const want = normaliseClass(className)
  return (standings?.cars ?? []).filter((c) => isRunning(c) && normaliseClass(c.class) === want).length
}

/**
 * Compare class names the way a person would.
 *
 * Mirrors normaliseClass in timing/internal/store/class.go. Vendors are not
 * consistent about case or spacing within a single session, and two spellings
 * of one class would otherwise produce two class leaders.
 */
function normaliseClass(s: string | undefined): string {
  return (s ?? '').trim().toLowerCase().split(/\s+/).join(' ')
}

/**
 * A stable colour slot for a class name.
 *
 * The obvious implementations are all wrong in the same way: assigning colours
 * in the order classes are first seen means the leader changing class recolours
 * the whole field, and assigning them in sort order means one new class shifts
 * everything after it. Either way the colour a person has learned to look for
 * stops meaning what it meant, which is worse than having no colour at all.
 *
 * So the slot is a pure function of the name. "EV3" is the same colour on every
 * screen, on every refresh, in every session, forever — and independent of who
 * is leading, who else is entered, and what order anything arrived in.
 *
 * FNV-1a because it is four lines and has no dependencies. Collisions are
 * possible and unimportant: two classes sharing a colour is a cosmetic
 * coincidence, whereas a colour that moves is a lie.
 */
export function classSlot(name: string | null | undefined, slots = 8): number {
  const s = (name ?? '').trim().toUpperCase()
  if (!s) return 0
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    // The FNV prime, by shifts, so this stays in 32-bit integer arithmetic
    // rather than drifting into floats.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0
  }
  return h % slots
}
